import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/admin/DashboardLayout';
import { 
  FileText, Mail, Phone, Calendar, Briefcase, 
  CheckCircle, XCircle, Clock, User, Trash2, Download, Filter
} from 'lucide-react';
import { useNotification } from '../../context/NotificationContext';
import DeleteConfirmationModal from '../../components/admin/DeleteConfirmationModal';

const ApplicantsManager = () => {
  const { addNotification } = useNotification();
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Delete State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchApplicants();
  }, []);

  const fetchApplicants = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/all_applicants.php`);
      setApplicants(res.data);
    } catch (err) {
      console.error(err);
      addNotification('Gagal memuat data pelamar', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/all_applicants.php`, {
        id, status: newStatus
      });
      setApplicants(prev => prev.map(app => app.id === id ? {...app, status: newStatus} : app));
      addNotification(`Status diperbarui menjadi ${newStatus}`, 'success');
    } catch (err) {
      addNotification('Gagal update status', 'error');
    }
  };

  const handleDelete = (id) => {
    setDeleteTargetId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    setDeleting(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/all_applicants.php?id=${deleteTargetId}`);
      setApplicants(prev => prev.filter(a => a.id !== deleteTargetId));
      addNotification('Data pelamar dihapus', 'success');
      setShowDeleteModal(false);
      setDeleteTargetId(null);
    } catch (err) {
        addNotification('Gagal menghapus data', 'error');
    } finally {
        setDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'accepted': return '#dcfce7'; // green-100
      case 'rejected': return '#fee2e2'; // red-100
      case 'interview': return '#dbeafe'; // blue-100
      case 'reviewed': return '#fef3c7'; // amber-100
      default: return '#f3f4f6'; // gray-100
    }
  };

  const getStatusTextColor = (status) => {
    switch(status) {
      case 'accepted': return '#166534'; // green-800
      case 'rejected': return '#991b1b'; // red-800
      case 'interview': return '#1e40af'; // blue-800
      case 'reviewed': return '#92400e'; // amber-800
      default: return '#374151'; // gray-700
    }
  };

  const filteredApplicants = filterStatus === 'all' 
    ? applicants 
    : applicants.filter(a => a.status === filterStatus);

  const styles = {
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' },
    title: { fontSize: '1.5rem', fontWeight: '700', color: '#111' },
    filterBar: { display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '1rem' },
    filterBtn: (active) => ({
      padding: '0.5rem 1rem',
      borderRadius: '2rem',
      border: active ? '1px solid #111' : '1px solid #e5e7eb',
      backgroundColor: active ? '#111' : '#fff',
      color: active ? '#fff' : '#374151',
      cursor: 'pointer',
      fontSize: '0.875rem',
      fontWeight: '500',
      whiteSpace: 'nowrap'
    }),
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1.5rem' },
    card: { backgroundColor: '#fff', borderRadius: '1rem', border: '1px solid #e5e7eb', overflow: 'hidden', transition: 'box-shadow 0.2s' },
    cardHeader: { padding: '1.5rem', borderBottom: '1px solid #f3f4f6' },
    cardBody: { padding: '1.5rem' },
    cardFooter: { padding: '1rem 1.5rem', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    
    row: { display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', fontSize: '0.9rem', color: '#4b5563' },
    name: { fontSize: '1.125rem', fontWeight: '700', color: '#111', marginBottom: '0.25rem' },
    jobTitle: { fontSize: '0.875rem', color: '#6b7280', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.4rem' },
    messageBox: { backgroundColor: '#f9fafb', padding: '1rem', borderRadius: '0.5rem', fontSize: '0.875rem', color: '#374151', marginTop: '1rem', fontStyle: 'italic' },
    
    statusSelect: { 
      padding: '0.4rem 0.75rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '500' 
    },
    cvBtn: { 
      display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#2563eb', textDecoration: 'none', fontSize: '0.875rem', fontWeight: '600', padding: '0.5rem 1rem', borderRadius: '0.5rem', backgroundColor: '#eff6ff' 
    },
    emptyState: { textAlign: 'center', padding: '4rem 1rem', color: '#6b7280' }
  };

  return (
    <DashboardLayout>
      <div style={styles.header}>
        <div>
            <h1 style={styles.title}>Pelamar Kerja</h1>
            <p style={{color:'#6b7280', marginTop:'0.25rem'}}>Kelola semua aplikasi masuk dari kandidat.</p>
        </div>
      </div>

      <div style={styles.filterBar}>
        <button style={styles.filterBtn(filterStatus === 'all')} onClick={() => setFilterStatus('all')}>Semua</button>
        <button style={styles.filterBtn(filterStatus === 'pending')} onClick={() => setFilterStatus('pending')}>Pending</button>
        <button style={styles.filterBtn(filterStatus === 'reviewed')} onClick={() => setFilterStatus('reviewed')}>Reviewed</button>
        <button style={styles.filterBtn(filterStatus === 'interview')} onClick={() => setFilterStatus('interview')}>Interview</button>
        <button style={styles.filterBtn(filterStatus === 'accepted')} onClick={() => setFilterStatus('accepted')}>Diterima</button>
        <button style={styles.filterBtn(filterStatus === 'rejected')} onClick={() => setFilterStatus('rejected')}>Ditolak</button>
      </div>

      {loading ? (
        <div style={{padding: '2rem', textAlign: 'center'}}>Loading data...</div>
      ) : filteredApplicants.length === 0 ? (
        <div style={styles.emptyState}>
            <User size={48} style={{marginBottom: '1rem', opacity: 0.5}} />
            <h3>Belum ada pelamar {filterStatus !== 'all' ? `dengan status ${filterStatus}` : ''}</h3>
        </div>
      ) : (
        <div style={styles.grid}>
          {filteredApplicants.map(app => (
            <div key={app.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start'}}>
                    <div>
                        <div style={styles.name}>{app.name}</div>
                        <div style={styles.jobTitle}>
                            <Briefcase size={14} /> 
                            Melamar: {app.job_title}
                        </div>
                    </div>
                    <div style={{
                        padding: '0.25rem 0.75rem', borderRadius: '99px', fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase',
                        backgroundColor: getStatusColor(app.status), color: getStatusTextColor(app.status)
                    }}>
                        {app.status}
                    </div>
                </div>
              </div>
              
              <div style={styles.cardBody}>
                {app.email && (
                    <div style={styles.row}><Mail size={16} /> {app.email}</div>
                )}
                {app.phone && (
                    <div style={styles.row}><Phone size={16} /> {app.phone}</div>
                )}
                <div style={styles.row}>
                    <Calendar size={16} /> 
                    {new Date(app.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                    <span style={{fontSize: '0.8rem', color: '#9ca3af'}}>({new Date(app.created_at).toLocaleTimeString('id-ID', {hour: '2-digit', minute:'2-digit'})})</span>
                </div>
                
                {app.message ? (
                    <div style={styles.messageBox}>
                        "{app.message}"
                    </div>
                ) : (
                    <div style={{...styles.messageBox, color: '#9ca3af', fontStyle: 'normal'}}>
                        Tidak ada pesan tambahan.
                    </div>
                )}
              </div>

              <div style={styles.cardFooter}>
                <a href={app.cv_url} target="_blank" rel="noreferrer" style={styles.cvBtn}>
                    <FileText size={16} /> CV
                </a>
                
                <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                    <select 
                        value={app.status} 
                        onChange={(e) => handleStatusUpdate(app.id, e.target.value)}
                        style={styles.statusSelect}
                    >
                        <option value="pending">Pending</option>
                        <option value="reviewed">Reviewed</option>
                        <option value="interview">Interview</option>
                        <option value="accepted">Accepted</option>
                        <option value="rejected">Rejected</option>
                    </select>
                    
                    <button 
                        onClick={() => handleDelete(app.id)}
                        style={{background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.25rem'}}
                        title="Hapus Pelamar"
                    >
                        <Trash2 size={18} />
                    </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        isConfirming={deleting}
        targetName="data pelamar ini"
      />
    </DashboardLayout>
  );
};

export default ApplicantsManager;
